<script setup lang="ts">
import type { EventPartnerFullDTO } from '~/shared/api/client/events/events.dto';
import { normalizeUrl } from '~/shared/utils/url';
import { resizeImageAttrs } from '~/shared/lib/resizeImage';

const props = defineProps<{
  partners: EventPartnerFullDTO[];
  erid: string | null;
  isPast: boolean;
}>();

const groupedPartners = computed(() => {
  const groups = new Map<
    string,
    { order: number; isLarge: boolean; items: EventPartnerFullDTO[] }
  >();

  for (const p of props.partners) {
    const key = p.partner_type.name;
    if (!groups.has(key)) {
      groups.set(key, {
        order: p.partner_type.order,
        isLarge: p.logo_size === 'large',
        items: [],
      });
    }
    groups.get(key)!.items.push(p);
  }

  return [...groups.entries()].sort((a, b) => a[1].order - b[1].order);
});

/* Logos are contained, so fit into the card box. */
const getLogoAttrs = (url: string, isLarge: boolean) =>
  resizeImageAttrs(
    url,
    isLarge ? { width: 320, height: 112 } : { width: 215, height: 111 },
  );
</script>

<template>
  <div v-if="partners.length" :class="$style.partners">
    <h2 :class="$style.title">Партнеры</h2>

    <div
      v-for="[typeName, group] in groupedPartners"
      :key="typeName"
      :class="$style.group"
    >
      <p :class="$style.groupTitle">{{ typeName }}</p>
      <div :class="$style.grid">
        <component
          :is="isPast ? 'div' : 'a'"
          v-for="partner in group.items"
          :key="partner.id"
          v-bind="
            isPast
              ? {}
              : {
                  href: normalizeUrl(partner.link || partner.partner.link),
                  target: '_blank',
                }
          "
          :class="[
            $style.card,
            {
              [$style.card_large!]: group.isLarge,
              [$style.card_static!]: isPast,
            },
          ]"
        >
          <img
            v-if="partner.partner.logo?.file_url"
            :src="
              getLogoAttrs(partner.partner.logo.file_url, group.isLarge).src
            "
            :srcset="
              getLogoAttrs(partner.partner.logo.file_url, group.isLarge).srcset
            "
            :alt="partner.partner.name"
            :class="$style.logo"
          />
          <div v-if="erid && !isPast" :class="$style.adLabel">
            <span :class="$style.adText">Реклама</span>
            <span v-if="partner.link_text" :class="$style.adLink">{{
              partner.link_text
            }}</span>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0 0 24px;
}

.group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.groupTitle {
  @include text_body1_medium;
  color: var(--color-black-2);
  margin: 0 0 24px;
}

.grid {
  display: flex;
  flex-wrap: wrap;
  max-width: calc(215px * 3 + 1px);
}

.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 215px;
  min-width: 160px;
  height: 111px;
  min-height: 80px;
  position: relative;
  border: 1px solid var(--color-gray-line);
  margin-top: -1px;
  margin-left: -1px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &_static:hover {
    opacity: 1;
  }

  &_large {
    width: 320px;
    height: 112px;
  }
}

.logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.adLabel {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  font-family: var(--font-golos, 'Golos Text', sans-serif);
  font-size: 10px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--color-gray-text);
}

.adText {
  flex-shrink: 0;
}

.adLink {
  text-align: right;
}

@media (max-width: 1379px) {
  .partners {
    padding-left: 20px;
    padding-right: 20px;
  }

  .title {
    font-size: 32px;
  }

  .card_large {
    width: 320px;
    height: 112px;
  }
}

@media (max-width: 1023px) {
  .title {
    font-size: 24px;
  }
}

@media (max-width: 767px) {
  .card_large {
    width: 320px;
    height: 112px;
  }
}

@media (max-width: 479px) {
  .title {
    font-size: 16px;
  }

  .card {
    width: calc(50% + 1px);
    height: 80px;
  }

  .card_large {
    width: 228px;
    height: 80px;
  }
}
</style>
